import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ToastProvider, useToastContext } from '@design-system/molecules/toast/Toast';
import { ToastProviderProps } from '@design-system/molecules/toast/toast.types';
import { removeToast, toastAdapter, ToastPayload } from 'src/reducers/slices/toast';
import { AppState } from 'src/reducers';
import { FormattedMessage } from 'react-intl';

const getToastTitle = (variant: 'success' | 'warning' | 'error' | 'info') => {
  switch (variant) {
    case 'success':
      return <FormattedMessage id="components.toast.success.title" />;
    case 'warning':
      return <FormattedMessage id="components.toast.warning.title" />;
    case 'error':
      return <FormattedMessage id="components.toast.error.title" />;
    case 'info':
      return <FormattedMessage id="components.toast.info.title" />;
  }
};

// Create a connector component that uses the toast context directly
const ReduxToastConnector: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  // Use the entity adapter's selectors
  const toastSelectors = toastAdapter.getSelectors();
  const toasts = useSelector((state: AppState) => toastSelectors.selectAll(state.toast));
  const { showToast } = useToastContext();
  const processedToastsRef = useRef<string[]>([]);

  useEffect(() => {
    // Process any new toasts in the queue
    toasts.forEach((toast: ToastPayload) => {
      if (toast.id && !processedToastsRef.current.includes(toast.id)) {
        // Mark this toast as processed
        processedToastsRef.current.push(toast.id);

        // Show the toast using the context
        showToast({
          ...toast,
          title: toast.title || getToastTitle(toast.variant),
          onClose: () => {
            // When toast is closed, remove it from Redux and from our processed list
            dispatch(removeToast(toast.id as string));
            processedToastsRef.current = processedToastsRef.current.filter((id) => id !== toast.id);
          },
        });
      }
    });
  }, [toasts, dispatch, showToast]);

  return <>{children}</>;
};

// Simplified component that wraps ToastProvider with our connector
export const ReduxToastProvider: React.FC<ToastProviderProps> = ({ children }) => (
  <ToastProvider>
    <ReduxToastConnector>{children}</ReduxToastConnector>
  </ToastProvider>
);
